import { supabase } from '@/lib/supabase';
import { PartAnalysis } from './types';

// Raw shape returned by the `lattice-analyze` edge function. The name is legacy
// (#309) — the function itself is a GENERIC part analyzer: it returns the bbox,
// a renderable STL, and (for STEP) topological edges, with nothing
// lattice-specific. We wrap it here so callers depend on `analyzePart`, never on
// the edge-function name or its snake_case wire shape.
interface LatticeAnalyzeResponse {
  dims?: number[];
  error?: string;
  previewUrl?: string;
  edges_b64?: string;
  tangent_edges_b64?: string;
}

/**
 * Measure an uploaded part by its `meshRef`. STL is parsed; STEP is tessellated
 * server-side and its B-rep edges extracted. Resolves to a normalized
 * {@link PartAnalysis}; throws on any failure so the caller can surface the
 * message (mirrors the contract the lattice converter relied on inline).
 */
export async function analyzePart(meshRef: string): Promise<PartAnalysis> {
  const { data, error } = await supabase.functions.invoke('lattice-analyze', {
    body: { meshRef },
  });
  const d = (data as LatticeAnalyzeResponse | null) ?? null;
  if (error || !d || d.error || !Array.isArray(d.dims) || d.dims.length !== 3) {
    throw new Error(
      d?.error ?? error?.message ?? 'Could not measure the part.',
    );
  }
  return {
    dims: [d.dims[0], d.dims[1], d.dims[2]],
    previewUrl: typeof d.previewUrl === 'string' ? d.previewUrl : '',
    edges: {
      edgesB64: typeof d.edges_b64 === 'string' ? d.edges_b64 : undefined,
      tangentEdgesB64:
        typeof d.tangent_edges_b64 === 'string'
          ? d.tangent_edges_b64
          : undefined,
    },
  };
}
